import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type {
  InvoiceStatusUi,
  ServiceBillingInvoice,
  ServiceBillingSummaryGroup,
} from '@/components/invoices/invoice.types'
import {
  getServiceBillingInvoices,
  rejectInvoice,
  approveInvoice,
  openInvoicePdf,
} from '@/api/invoices.api'
import { useEffect, useState } from 'react'
import { RejectInvoiceDialog } from './RejectInvoiceDialog'
import { InvoiceAuditLog } from './InvoiceAuditLog'
import { mapInvoiceStatus } from '../domain/statusMapping'
import { LuChevronLeft, LuChevronsUp } from 'react-icons/lu'
import { toaster } from '../../../../components/ui/toaster/toasterInstance'

// Pilot is a service billed to the union itself. It is shown only when the union
// appears as a recipient (a perCustomer key) on the invoice, and only in that
// row — never for member banks. If this name ever varies per tenant, replace
// this constant with a backend-provided flag on the summary group.
const UNION_CUSTOMER_NAME = 'Union Investment Privatfonds GmbH'

function formatDate(value?: string | null) {
  if (!value) return '-'
  return new Intl.DateTimeFormat('de-DE').format(new Date(value))
}

function formatPeriod(start?: string | null, end?: string | null) {
  if (!start || !end) return '-'
  const s = new Intl.DateTimeFormat('de-DE').format(new Date(start))
  const e = new Intl.DateTimeFormat('de-DE').format(new Date(end))
  return `${s}-${e}`
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatUnits(value: number) {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// DRAFT and ERROR invoices are not yet part of the backend's annual sum (it
// only counts FINALIZED/SENT/ARCHIVED): a draft is a preview, an ERROR invoice
// failed before confirmation. Their own units are added at display time — the
// same rule the backend uses for the PDF page-3 breakdown.
function isPendingInvoiceStatus(status: InvoiceStatusUi) {
  return status === 'DRAFT' || status === 'ERROR'
}

// "Geleistet" is the year-to-date after this invoice. The backend's
// annualInvoicedUnits sums all FINALIZED/SENT/ARCHIVED invoices up to and
// including this invoice's month — so once this invoice is confirmed, its own
// units are already inside that sum, and adding invoicedUnits again counted
// the current invoice's units twice (SIB-923: 28 TOJ displayed as 56). Only a
// pending (DRAFT/ERROR) invoice is missing from the backend sum and needs its
// units added on top. A CANCELED invoice's units never count toward the year.
function getDisplayedPerformedUnits(
  annualInvoicedUnits: number,
  currentInvoiceUnits: number,
  invoiceStatus: InvoiceStatusUi
) {
  return isPendingInvoiceStatus(invoiceStatus)
    ? annualInvoicedUnits + currentInvoiceUnits
    : annualInvoicedUnits
}

function DeltaBadge({ value }: { value: number }) {
  if (value <= 0) return null

  return (
    <HStack
      as="span"
      display="inline-flex"
      gap={1}
      px={2}
      minH="28px"
      borderRadius="999px"
      bg="green.subtle"
      color="green.fg"
      fontSize="14px"
      fontWeight="500"
      lineHeight="1"
      whiteSpace="nowrap"
    >
      <Box as={LuChevronsUp} boxSize="14px" />
      <Text as="span">{formatUnits(value)}</Text>
    </HStack>
  )
}

function SummaryTable({
  title,
  amount,
  summary,
  invoiceStatus,
  embedded = false,
  showPilot = false,
}: {
  title: string
  amount?: number
  summary: ServiceBillingSummaryGroup
  invoiceStatus: InvoiceStatusUi
  embedded?: boolean
  showPilot?: boolean
}) {
  return (
    <Box
      borderWidth={embedded ? '0' : '1px'}
      borderColor="border.emphasized"
      borderRadius={embedded ? '0' : '16px'}
      bg={embedded ? 'transparent' : 'bg.default'}
      px={embedded ? 0 : 4}
      py={embedded ? 0 : 4}
    >
      {!embedded && (
        <Flex align="center" justify="space-between" mb={4}>
          <Text fontWeight="500" color="fg">
            {title}
          </Text>

          {typeof amount === 'number' ? (
            <Text fontWeight="500" color="fg">
              {formatCurrency(amount)}
            </Text>
          ) : null}
        </Flex>
      )}

      <Box
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="12px"
        overflow="hidden"
        bg={embedded ? 'bg.default' : undefined}
      >
        <Table.Root variant="line" size="sm">
          <Table.Header>
            <Table.Row bg="bg.default">
              <Table.ColumnHeader
                w="20%"
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
                color="fg"
                fontWeight="600"
              />
              <Table.ColumnHeader
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
                color="fg"
                background="bg.muted"
                fontWeight="600"
              >
                Abgerechnet
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
                color="fg"
                fontWeight="600"
              >
                Storniert
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
                color="fg"
                fontWeight="600"
              >
                Bestellt
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderColor="border.default"
                color="fg"
                fontWeight="600"
              >
                Geleistet
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
              >
                <Text fontSize="sm" fontWeight="medium" color="fg">
                  TOJ
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
                background="bg.muted"
                color="fg.success"
              >
                <Text fontSize="sm" fontWeight="normal" color="fg.success">
                  {formatUnits(summary.toj.invoicedUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
              >
                <Text fontSize="sm" fontWeight="normal" color="fg">
                  {formatUnits(summary.toj.cancelledUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
              >
                <Text fontSize="sm" fontWeight="normal" color="fg">
                  {formatUnits(summary.toj.annualOrderedUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell px={4} py={3} borderBottomWidth="1px" borderColor="border.default">
                <HStack gap={2}>
                  <Text>
                    {formatUnits(
                      getDisplayedPerformedUnits(
                        summary.toj.annualInvoicedUnits,
                        summary.toj.invoicedUnits,
                        invoiceStatus
                      )
                    )}
                  </Text>
                  <DeltaBadge value={summary.toj.invoicedUnits} />
                </HStack>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
              >
                <Text fontSize="sm" fontWeight="medium" color="fg">
                  Beratung
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
                background="bg.muted"
                color="fg.success"
              >
                <Text fontSize="sm" fontWeight="normal" color="fg.success">
                  {formatUnits(summary.consulting.invoicedUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
              >
                <Text fontSize="sm" fontWeight="normal" color="fg">
                  {formatUnits(summary.consulting.cancelledUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderRightWidth="1px"
                borderColor="border.default"
              >
                <Text fontSize="sm" fontWeight="normal" color="fg">
                  {formatUnits(summary.consulting.annualOrderedUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell px={4} py={3} borderBottomWidth="1px" borderColor="border.default">
                <HStack gap={2}>
                  <Text>
                    {formatUnits(
                      getDisplayedPerformedUnits(
                        summary.consulting.annualInvoicedUnits,
                        summary.consulting.invoicedUnits,
                        invoiceStatus
                      )
                    )}
                  </Text>
                  <DeltaBadge value={summary.consulting.invoicedUnits} />
                </HStack>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell
                px={4}
                py={3}
                borderRightWidth="1px"
                borderColor="border.default"
                borderBottomWidth={showPilot ? '1px' : undefined}
              >
                <Text fontSize="sm" fontWeight="medium" color="fg">
                  Schulung
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderRightWidth="1px"
                borderColor="border.default"
                borderBottomWidth={showPilot ? '1px' : undefined}
                background="bg.muted"
                color="fg.success"
              >
                <Text fontSize="sm" fontWeight="normal" color="fg.success">
                  {formatUnits(summary.training.invoicedUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderRightWidth="1px"
                borderColor="border.default"
                borderBottomWidth={showPilot ? '1px' : undefined}
              >
                <Text fontSize="sm" fontWeight="normal" color="fg">
                  {formatUnits(summary.training.cancelledUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell
                px={4}
                py={3}
                borderRightWidth="1px"
                borderColor="border.default"
                borderBottomWidth={showPilot ? '1px' : undefined}
              >
                <Text fontSize="sm" fontWeight="normal" color="fg">
                  {formatUnits(summary.training.annualOrderedUnits)}
                </Text>
              </Table.Cell>
              <Table.Cell px={4} py={3} borderBottomWidth={showPilot ? '1px' : undefined}>
                <HStack gap={2}>
                  <Text>
                    {formatUnits(
                      getDisplayedPerformedUnits(
                        summary.training.annualInvoicedUnits,
                        summary.training.invoicedUnits,
                        invoiceStatus
                      )
                    )}
                  </Text>
                  <DeltaBadge value={summary.training.invoicedUnits} />
                </HStack>
              </Table.Cell>
            </Table.Row>

            {showPilot && (
              <Table.Row>
                <Table.Cell px={4} py={3} borderRightWidth="1px" borderColor="border.default">
                  <Text fontSize="sm" fontWeight="medium" color="fg">
                    Pilot
                  </Text>
                </Table.Cell>
                <Table.Cell
                  px={4}
                  py={3}
                  borderRightWidth="1px"
                  borderColor="border.default"
                  background="bg.muted"
                  color="fg.success"
                >
                  <Text fontSize="sm" fontWeight="normal" color="fg.success">
                    {formatUnits(summary.pilot?.invoicedUnits ?? 0)}
                  </Text>
                </Table.Cell>
                <Table.Cell px={4} py={3} borderRightWidth="1px" borderColor="border.default">
                  <Text fontSize="sm" fontWeight="normal" color="fg">
                    {formatUnits(summary.pilot?.cancelledUnits ?? 0)}
                  </Text>
                </Table.Cell>
                <Table.Cell px={4} py={3} borderRightWidth="1px" borderColor="border.default">
                  <Text fontSize="sm" fontWeight="normal" color="fg">
                    {formatUnits(summary.pilot?.annualOrderedUnits ?? 0)}
                  </Text>
                </Table.Cell>
                <Table.Cell px={4} py={3}>
                  <HStack gap={2}>
                    <Text>
                      {formatUnits(
                        getDisplayedPerformedUnits(
                          summary.pilot?.annualInvoicedUnits ?? 0,
                          summary.pilot?.invoicedUnits ?? 0,
                          invoiceStatus
                        )
                      )}
                    </Text>
                    <DeltaBadge value={summary.pilot?.invoicedUnits ?? 0} />
                  </HStack>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  )
}

export function ServiceBillingInvoiceReviewPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { invoiceId } = useParams()

  const stateInvoice = (location.state as { invoice?: ServiceBillingInvoice } | null)?.invoice
  const [invoice, setInvoice] = useState<ServiceBillingInvoice | null>(stateInvoice ?? null)
  const [isLoading, setIsLoading] = useState(!stateInvoice)

  const [isRejectOpen, setIsRejectOpen] = useState(false)
  const [isRejectLoading, setIsRejectLoading] = useState(false)
  const [rejectError, setRejectError] = useState<string | null>(null)
  const [isApproveLoading, setIsApproveLoading] = useState(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)

  async function handlePreview(id: string) {
    setIsPreviewLoading(true)
    try {
      await openInvoicePdf(id)
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: 'Vorschau nicht möglich',
        description: error?.message || 'Die PDF-Vorschau konnte nicht geöffnet werden.',
        closable: true,
      })
    } finally {
      setIsPreviewLoading(false)
    }
  }

  useEffect(() => {
    if (!invoiceId) return

    let mounted = true

    async function load() {
      try {
        setIsLoading(true)
        const data = await getServiceBillingInvoices({})
        const found = data.items.find(x => String(x.id) === String(invoiceId)) ?? null

        if (mounted) {
          setInvoice(found)
        }
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    load()

    return () => {
      mounted = false
    }
  }, [invoiceId])

  if (isLoading) {
    return <Box p={8}>Lädt...</Box>
  }

  if (!invoice) {
    return <Box p={8}>Rechnung nicht gefunden.</Box>
  }

  const unitPrices = {
    toj: invoice.items?.find(x => x.serviceType === 'TOJ')?.grossRate ?? 0,
    consulting: invoice.items?.find(x => x.serviceType === 'CONSULTING')?.grossRate ?? 0,
    training: invoice.items?.find(x => x.serviceType === 'TRAINING')?.grossRate ?? 0,
    pilot: invoice.items?.find(x => x.serviceType === 'PILOT')?.grossRate ?? 0,
  }
  const hasPilotItems = (invoice.items ?? []).some(x => x.serviceType === 'PILOT')

  // When the backend omits summary.pilot, synthesize it from invoice items so the Pilot row is visible.
  const pilotItems = (invoice.items ?? []).filter(x => x.serviceType === 'PILOT')
  const summaryWithPilot = (() => {
    if (!invoice.summary) return null
    if (pilotItems.length === 0) return invoice.summary

    const synthPilot = (items: typeof pilotItems) => {
      const invoicedUnits = items.reduce((s, i) => s + (i.quantity ?? 0), 0)
      return {
        invoicedUnits,
        cancelledUnits: 0,
        annualOrderedUnits: 0,
        // The backend gave us no pilot year-to-date. On a confirmed invoice the
        // items are part of the year's invoiced units, so they are the best
        // available annual value; a pending invoice's units get added at
        // display time instead (keep in sync with getDisplayedPerformedUnits).
        annualInvoicedUnits: isPendingInvoiceStatus(invoice.status) ? 0 : invoicedUnits,
      }
    }

    const augmentGroup = (
      group: (typeof invoice.summary)['total'],
      groupItems: typeof pilotItems
    ) => (group.pilot ? group : { ...group, pilot: synthPilot(groupItems) })

    const total = augmentGroup(invoice.summary.total, pilotItems)
    const perCustomer = Object.fromEntries(
      Object.entries(invoice.summary.perCustomer).map(([name, group]) => {
        const customerItems = pilotItems.filter(
          i => (i.serviceRecipientName?.trim() ?? '') === name
        )
        return [name, augmentGroup(group, customerItems)]
      })
    )
    return { ...invoice.summary, total, perCustomer }
  })()

  // Pilot is shown only when the union itself is a recipient on this invoice,
  // i.e. it appears as a perCustomer key. Member banks never show a Pilot row.
  const unionIsRecipient = Boolean(summaryWithPilot?.perCustomer?.[UNION_CUSTOMER_NAME])

  const grossAmountByRecipient = (invoice.items ?? []).reduce(
    (acc, item) => {
      const key = item.serviceRecipientName?.trim()
      if (!key) return acc
      acc[key] = (acc[key] ?? 0) + (item.grossAmount ?? 0)
      return acc
    },
    {} as Record<string, number>
  )

  const isPending = invoice.approvalStatus === 'PENDING_APPROVAL'

  // Single source of truth for the status chips (shared with the dashboard table),
  // so already-processed invoices (sent, canceled, draft) are labelled correctly.
  const headerStatus = mapInvoiceStatus(
    invoice.approvalStatus,
    Boolean(invoice.sentAt),
    invoice.status === 'DRAFT',
    invoice.status === 'CANCELED'
  )

  return (
    <Box px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }} pb={{ base: 28, md: 32 }}>
      <Box maxW="1120px" mx="auto">
        <Button mb={8} variant="outline" color="fg" onClick={() => navigate(-1)}>
          <LuChevronLeft></LuChevronLeft>
          Zurück
        </Button>

        <Text fontSize="36px" lineHeight="1.2" fontWeight="700" mb={8} color="fg">
          Rechnung prüfen
        </Text>

        <Grid templateColumns={{ base: '1fr', xl: '2.1fr 1fr' }} gap={6} alignItems="start" mb={8}>
          <GridItem>
            <Box
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="16px"
              bg="bg.default"
              px={4}
              py={5}
            >
              <Flex justify="space-between" align="start" gap={4} mb={6}>
                <Box>
                  <Text fontSize="lg" lineHeight="1.1" fontWeight="normal" color="fg">
                    {invoice.invoiceNumber}
                  </Text>

                  <HStack mt={2} gap={2} align="center">
                    <Box w="10px" h="10px" borderRadius="full" bg="blue.500" />
                    <Text color="blue.500" fontSize="xs" fontWeight="normal">
                      {invoice.scope === 'ACTIVITY' ? 'Aktivität' : 'Bestellung'}
                    </Text>
                    <Text color="fg.muted" fontSize="xs">
                      |
                    </Text>
                    <Text color="fg.muted" fontSize="xs">
                      {invoice.bankName}
                    </Text>
                  </HStack>
                </Box>

                <Box textAlign="right">
                  <Text fontSize="lg" lineHeight="1.1" fontWeight="normal" color="fg">
                    {formatCurrency(invoice.totalGross)}
                  </Text>

                  <Flex justify="end" mt={2}>
                    <Button
                      size="xs"
                      bg="fg.default"
                      color="bg.default"
                      borderRadius="999px"
                      px={3}
                      loading={isPreviewLoading}
                      _hover={{ opacity: 0.85 }}
                      onClick={() => handlePreview(String(invoice.id))}
                    >
                      Vorschau
                    </Button>
                  </Flex>

                  <HStack justify="end" mt={2} gap={2}>
                    {headerStatus && (
                      <>
                        <Badge
                          colorPalette={headerStatus.labelPalette ?? 'gray'}
                          variant="solid"
                          borderRadius="999px"
                          px={3}
                          py={1}
                          fontWeight="500"
                        >
                          {headerStatus.label}
                        </Badge>

                        {headerStatus.sublabel && (
                          <Badge
                            colorPalette={headerStatus.sublabelPalette ?? 'gray'}
                            variant="subtle"
                            borderRadius="999px"
                            px={3}
                            py={1}
                            fontWeight="500"
                          >
                            {headerStatus.sublabel}
                          </Badge>
                        )}
                      </>
                    )}
                  </HStack>
                </Box>
              </Flex>

              {invoice.approvalStatus === 'REJECTED' && invoice.rejectionReason && (
                <Box
                  mb={6}
                  px={4}
                  py={3}
                  borderRadius="8px"
                  bg="red.subtle"
                  borderWidth="1px"
                  borderColor="red.muted"
                >
                  <Text fontSize="sm" fontWeight="600" color="red.fg" mb={1}>
                    Ablehnungsgrund
                  </Text>
                  <Text fontSize="sm" color="fg">
                    {invoice.rejectionReason}
                  </Text>
                </Box>
              )}

              <HStack gap={{ base: 8, md: 14 }} align="start">
                <Box>
                  <Text color="fg.muted" fontSize="xs" mb={1}>
                    Ausstellungsdatum
                  </Text>
                  <Text fontSize="xs" fontWeight="500" color="fg">
                    {formatDate(invoice.createdAt)}
                  </Text>
                </Box>

                <Box>
                  <Text color="fg.muted" fontSize="xs" mb={1}>
                    Leistungszeitraum
                  </Text>
                  <Text fontSize="xs" fontWeight="500" color="fg">
                    {formatPeriod(invoice.periodStart, invoice.periodEnd)}
                  </Text>
                </Box>
              </HStack>

              {summaryWithPilot?.total && (
                <Box mt={8}>
                  <SummaryTable
                    title={invoice.bankName}
                    amount={invoice.totalGross}
                    summary={summaryWithPilot.total}
                    invoiceStatus={invoice.status}
                    embedded
                    showPilot={unionIsRecipient}
                  />
                </Box>
              )}
            </Box>
          </GridItem>

          <GridItem>
            <Box
              borderWidth="1px"
              borderColor="border.emphasized"
              borderRadius="16px"
              bg="bg.panel"
              px={4}
              py={5}
            >
              <Text fontSize="18px" lineHeight="1.2" fontWeight="normal" color="fg">
                {invoice.consultantName}
              </Text>

              <Text color="fg.muted" fontSize="14px" mt={1} mb={6}>
                Preise pro Einheit
              </Text>

              <Grid templateColumns={hasPilotItems ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)'} gap={6}>
                <Box>
                  <Text color="fg.muted" fontSize="15px" mb={1}>
                    TOJ
                  </Text>
                  <Text fontSize="20px" fontWeight="600" color="fg.muted">
                    {formatCurrency(unitPrices.toj)}
                  </Text>
                </Box>

                <Box>
                  <Text color="fg.muted" fontSize="15px" mb={1}>
                    Beratung
                  </Text>
                  <Text fontSize="20px" fontWeight="600" color="fg.muted">
                    {formatCurrency(unitPrices.consulting)}
                  </Text>
                </Box>

                <Box>
                  <Text color="fg.muted" fontSize="15px" mb={1}>
                    Training
                  </Text>
                  <Text fontSize="20px" fontWeight="600" color="fg.muted">
                    {formatCurrency(unitPrices.training)}
                  </Text>
                </Box>

                {hasPilotItems && (
                  <Box>
                    <Text color="fg.muted" fontSize="15px" mb={1}>
                      Pilot
                    </Text>
                    <Text fontSize="20px" fontWeight="600" color="fg.muted">
                      {formatCurrency(unitPrices.pilot)}
                    </Text>
                  </Box>
                )}
              </Grid>
            </Box>

            <Box mt={6}>
              <InvoiceAuditLog invoiceId={String(invoice.id)} />
            </Box>
          </GridItem>
        </Grid>

        <Box mt={8} w={{ base: 'full', xl: 'calc((100% - 24px) * 2.1 / 3.1)' }}>
          <VStack align="stretch" gap={8}>
            {Object.entries(summaryWithPilot?.perCustomer ?? {}).map(([customerName, summary]) => (
              <SummaryTable
                key={customerName}
                title={customerName}
                amount={grossAmountByRecipient[customerName]}
                summary={summary}
                invoiceStatus={invoice.status}
                showPilot={customerName === UNION_CUSTOMER_NAME}
              />
            ))}
          </VStack>
        </Box>

        <Flex
          position="fixed"
          bottom={6}
          left="50%"
          transform="translateX(-50%)"
          zIndex={20}
          justify="center"
          gap={3}
          bg="bg"
          px={4}
          py={3}
          borderRadius="full"
          borderWidth="1px"
          borderColor="border"
          boxShadow="lg"
        >
          <Button
            colorPalette="red"
            variant="solid"
            minW="120px"
            borderRadius="8px"
            disabled={!isPending}
            onClick={() => {
              if (!isPending) return
              setRejectError(null)
              setIsRejectOpen(true)
            }}
          >
            Ablehnen
          </Button>

          <Button
            colorPalette="green"
            variant="solid"
            minW="120px"
            borderRadius="8px"
            loading={isApproveLoading}
            disabled={!isPending}
            onClick={async () => {
              if (!isPending) return
              const toastId = toaster.create({
                type: 'loading',
                title: 'Rechnung wird freigegeben...',
              })

              try {
                setIsApproveLoading(true)

                await approveInvoice(String(invoice.id))

                toaster.update(toastId, {
                  type: 'success',
                  title: 'Rechnung wurde freigegeben',
                  description: 'Die Rechnung wurde erfolgreich freigegeben.',
                  closable: true,
                })
                setTimeout(() => {
                  navigate(-1)
                }, 300)
              } catch (error: any) {
                toaster.update(toastId, {
                  type: 'error',
                  title: 'Fehler',
                  description:
                    error?.response?.data?.message || 'Rechnung konnte nicht freigegeben werden.',
                  closable: true,
                })
              } finally {
                setIsApproveLoading(false)
              }
            }}
          >
            Freigeben
          </Button>
        </Flex>
        <RejectInvoiceDialog
          isOpen={isRejectOpen}
          loading={isRejectLoading}
          error={rejectError}
          onClose={() => {
            if (isRejectLoading) return
            setIsRejectOpen(false)
            setRejectError(null)
          }}
          onConfirm={async (reason: string) => {
            const toastId = toaster.create({
              type: 'loading',
              title: 'Rechnung wird abgelehnt...',
            })

            try {
              setRejectError(null)
              setIsRejectLoading(true)

              await rejectInvoice(String(invoice.id), {
                rejectionReason: reason,
              })

              setIsRejectOpen(false)

              toaster.update(toastId, {
                type: 'error',
                title: 'Rechnung wurde abgelehnt',
                description:
                  'Die Begründung wurde an den SIB weitergeleitet. Diese Rechnung wurde automatisch ins Archiv verschoben.',
                closable: true,
              })

              setTimeout(() => {
                navigate(-1)
              }, 500)
            } catch (error: any) {
              toaster.update(toastId, {
                type: 'error',
                title: 'Fehler',
                description:
                  error?.response?.data?.message || 'Rechnung konnte nicht abgelehnt werden.',
                closable: true,
              })

              setRejectError(null)
            } finally {
              setIsRejectLoading(false)
            }
          }}
        />
      </Box>
    </Box>
  )
}
